import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';
import { cx } from './cx';
import { focusableIn } from './focus';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** alertdialog for confirmations that interrupt (APG "Alert Dialog"). */
  role?: 'dialog' | 'alertdialog';
  /** Element focused on open; defaults to the first focusable element, else the panel. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** false = clicking the backdrop does not close (Esc and the × still do). */
  dismissOnBackdrop?: boolean;
  closeLabel?: string;
}

const SIZES = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-3xl' } as const;

/**
 * Modal dialog (APG "Dialog (Modal)"): rendered in a portal, labelled by its title, focus moved
 * inside and trapped, Esc closes, focus returns to the opener, the page behind is `inert` and
 * does not scroll. Full-width sheet on phones, centred panel from `sm`.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  role = 'dialog',
  initialFocus,
  dismissOnBackdrop = true,
  closeLabel = 'Close',
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const id = useId().replace(/:/g, '');

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const host = hostRef.current;
    // everything else in <body> becomes inert (not focusable, hidden from AT) while open
    const siblings = [...document.body.children].filter(
      (el) => el !== host && !el.hasAttribute('inert') && !el.hasAttribute('data-inert-exempt'),
    );
    siblings.forEach((el) => el.setAttribute('inert', ''));
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    const target =
      initialFocus?.current ??
      (panel && focusableIn(panel).find((el) => !el.dataset.dialogClose)) ??
      panel;
    target?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      } else if (e.key === 'Tab' && panel) {
        const els = focusableIn(panel);
        if (!els.length) {
          e.preventDefault();
          return;
        }
        const first = els[0]!;
        const last = els.at(-1)!;
        if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      siblings.forEach((el) => el.removeAttribute('inert'));
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, [open, initialFocus]);

  if (!open) return null;
  return createPortal(
    <div
      ref={hostRef}
      className="fixed inset-0 z-(--z-modal) flex items-end justify-center sm:items-center sm:p-4"
    >
      <div
        aria-hidden="true"
        data-testid="dialog-backdrop"
        className="absolute inset-0 bg-black/50"
        onClick={() => dismissOnBackdrop && onClose()}
      />
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-desc` : undefined}
        tabIndex={-1}
        className={cx(
          'relative flex max-h-[90dvh] w-full flex-col rounded-ui bg-surface shadow-modal outline-none',
          SIZES[size],
        )}
      >
        <div className="flex items-start gap-3 border-b border-border px-4 py-3">
          <h2 id={`${id}-title`} className="flex-1 text-xl font-light text-heading">
            {title}
          </h2>
          <button
            type="button"
            data-dialog-close="true"
            aria-label={closeLabel}
            onClick={onClose}
            className="-me-1 flex size-8 items-center justify-center text-ink-muted hover:text-ink"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-3">
          {description && (
            <p id={`${id}-desc`} className="mb-3 text-sm text-ink">
              {description}
            </p>
          )}
          {children}
        </div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-border bg-surface-muted px-4 py-3 sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Confirmation (alertdialog). Focus starts on Cancel so Enter never confirms a destructive action by accident. */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger,
  busy,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: ReactNode;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      role="alertdialog"
      size="sm"
      title={title}
      description={message}
      initialFocus={cancelRef}
      dismissOnBackdrop={false}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
